import axios from 'axios';

// ============================================================
// BASE AXIOS INSTANCE
// ============================================================
const BASE_URL = 'https://inr-voua.onrender.com';
export { BASE_URL };

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // CRITICAL FIX: when sending FormData (file uploads), let the browser set
  // Content-Type itself. The browser/axios auto-generates a boundary token
  // like "multipart/form-data; boundary=----WebKitFormBoundary...". If we
  // manually set "Content-Type: multipart/form-data" without a boundary,
  // the backend cannot parse the body and will reject the request (usually
  // as a 400/422 with an unhelpful message). Deleting the default JSON
  // header here lets axios compute the correct one automatically.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Always log the full backend error to console so real failures are
    // visible instead of silently swallowed. Check DevTools console for
    // this whenever a request fails.
    console.error(
      `[API ERROR] ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`,
      '\nStatus:', error.response?.status,
      '\nResponse body:', error.response?.data,
      '\nMessage:', error.message
    );

    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/')) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const res = await api.post('/auth/refresh', { refresh_token: refreshToken });
        const newToken = res.data.access_token || res.data.token;
        localStorage.setItem('authToken', newToken);
        if (res.data.refresh_token) localStorage.setItem('refreshToken', res.data.refresh_token);
        isRefreshing = false;
        onRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

const withParams = (params) => ({ params });

// ============================================================
// AUTH
// ============================================================
export const authAPI = {
  getCaptcha: () => api.get('/auth/captcha'),
  login: ({ role, email, password, captcha_token, captcha_answer }) =>
    api.post('/auth/login', { role, email, password, captcha_token, captcha_answer }),
  refresh: (refresh_token) => api.post('/auth/refresh', { refresh_token }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  getMe: () => api.get('/auth/me'),
};

// ============================================================
// FILES
// ============================================================
export const fileAPI = {
  upload: (file, subfolder) => {
    const formData = new FormData();
    formData.append('file', file);
    const query = subfolder ? `?subfolder=${subfolder}` : '';
    // No manual Content-Type header here — the request interceptor above
    // strips any default header for FormData bodies so the browser sets
    // the correct multipart boundary automatically.
    return api.post(`/files/upload${query}`, formData);
  },
};

// ============================================================
// VOTERS
// ============================================================
export const votersAPI = {
  getAll: (params = {}) => api.get('/voters', withParams(params)),
  create: (data) => api.post('/voters', data),
  summaryByMandal: () => api.get('/voters/summary/by-mandal'),
  genderDistribution: () => api.get('/voters/gender-distribution'),
  getById: (voter_id) => api.get(`/voters/${voter_id}`),
  update: (voter_id, data) => api.put(`/voters/${voter_id}`, data),
  delete: (voter_id) => api.delete(`/voters/${voter_id}`),
  bulkUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/voters/bulk-upload', formData);
  },
  revealAadhaar: (voter_id) => api.get(`/voters/${voter_id}/aadhaar/reveal`),
};

// ============================================================
// BOOTHS
// ============================================================
export const boothsAPI = {
  getAll: (params = {}) => api.get('/booths', withParams(params)),
  create: (data) => api.post('/booths', data),
  getById: (item_id) => api.get(`/booths/${item_id}`),
  update: (item_id, data) => api.put(`/booths/${item_id}`, data),
  delete: (item_id) => api.delete(`/booths/${item_id}`),
};

// ============================================================
// DEVELOPMENT WORKS
// ============================================================
export const developmentWorksAPI = {
  statusSummary: () => api.get('/development-works/status-summary'),
  bulkUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/development-works/bulk-upload', formData);
  },
  getAll: (params = {}) => api.get('/development-works', withParams(params)),
  create: (data) => api.post('/development-works', data),
  getById: (item_id) => api.get(`/development-works/${item_id}`),
  update: (item_id, data) => api.put(`/development-works/${item_id}`, data),
  delete: (item_id) => api.delete(`/development-works/${item_id}`),
};

// ============================================================
// SCHEMES (MASTER)
// ============================================================
export const schemesAPI = {
  getAll: (params = {}) => api.get('/schemes', withParams(params)),
  create: (data) => api.post('/schemes', data),
  getById: (item_id) => api.get(`/schemes/${item_id}`),
  update: (item_id, data) => api.put(`/schemes/${item_id}`, data),
  delete: (item_id) => api.delete(`/schemes/${item_id}`),
};

// ============================================================
// BENEFICIARIES — PER-SCHEME MODULES
// ============================================================
const makeSchemeAPI = (basePath) => ({
  getAll: (params = {}) => api.get(basePath, withParams(params)),
  create: (data) => api.post(basePath, data),
  getById: (item_id) => api.get(`${basePath}/${item_id}`),
  update: (item_id, data) => api.put(`${basePath}/${item_id}`, data),
  delete: (item_id) => api.delete(`${basePath}/${item_id}`),
  bulkUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`${basePath}/bulk-upload`, formData);
  },
});

export const cmrfAPI = makeSchemeAPI('/beneficiaries/cmrf');
export const aadabiddaNidhiAPI = makeSchemeAPI('/beneficiaries/aadabidda-nidhi');
export const thallikiVandanamAPI = makeSchemeAPI('/beneficiaries/thalliki-vandanam');
export const deepamSchemeAPI = makeSchemeAPI('/beneficiaries/deepam-scheme');
export const mahaShakthiAPI = makeSchemeAPI('/beneficiaries/maha-shakthi');
export const annadataSukhibhavaAPI = makeSchemeAPI('/beneficiaries/annadata-sukhibhava');
export const yuvagalamAPI = makeSchemeAPI('/beneficiaries/yuvagalam');

// ============================================================
// BENEFICIARIES — CROSS-SCHEME
// ============================================================
export const beneficiariesAPI = {
  getAll: (params = {}) => api.get('/beneficiaries', withParams(params)),
  bulkUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/beneficiaries/bulk-upload', formData);
  },
  lookup: (epic_no) => api.get(`/beneficiaries/lookup/${epic_no}`),
  byGeography: (params = {}) => api.get('/beneficiaries/by-geography', withParams(params)),
  getById: (beneficiary_id) => api.get(`/beneficiaries/${beneficiary_id}`),
  delete: (beneficiary_id) => api.delete(`/beneficiaries/${beneficiary_id}`),
  revealAadhaar: (beneficiary_id) => api.get(`/beneficiaries/${beneficiary_id}/aadhaar/reveal`),
};

export const SCHEME_SLUGS = {
  CMRF: 'cmrf',
  AADABIDDA_NIDHI: 'aadabidda-nidhi',
  THALLIKI_VANDANAM: 'thalliki-vandanam',
  DEEPAM_SCHEME: 'deepam-scheme',
  MAHA_SHAKTHI: 'maha-shakthi',
  ANNADATA_SUKHIBHAVA: 'annadata-sukhibhava',
  YUVAGALAM: 'yuvagalam',
};

// ============================================================
// MULTIMEDIA — GALLERY
// ============================================================
export const galleryAPI = {
  getAll: (params = {}) => api.get('/gallery', withParams(params)),
  create: (data) => api.post('/gallery', data),
  getById: (item_id) => api.get(`/gallery/${item_id}`),
  update: (item_id, data) => api.put(`/gallery/${item_id}`, data),
  delete: (item_id) => api.delete(`/gallery/${item_id}`),
};

// ============================================================
// MULTIMEDIA — MP3
// ============================================================
export const mp3API = {
  incrementPlay: (item_id) => api.post(`/mp3/${item_id}/play`),
  incrementDownload: (item_id) => api.post(`/mp3/${item_id}/download`),
  getAll: (params = {}) => api.get('/mp3', withParams(params)),
  create: (data) => api.post('/mp3', data),
  getById: (item_id) => api.get(`/mp3/${item_id}`),
  update: (item_id, data) => api.put(`/mp3/${item_id}`, data),
  delete: (item_id) => api.delete(`/mp3/${item_id}`),
};

// ============================================================
// MULTIMEDIA — VIDEOS
// ============================================================
export const videosAPI = {
  getAll: (params = {}) => api.get('/videos', withParams(params)),
  create: (data) => api.post('/videos', data),
  getById: (item_id) => api.get(`/videos/${item_id}`),
  update: (item_id, data) => api.put(`/videos/${item_id}`, data),
  delete: (item_id) => api.delete(`/videos/${item_id}`),
};

// ============================================================
// MULTIMEDIA — PRESS
// ============================================================
export const pressAPI = {
  getAll: (params = {}) => api.get('/press', withParams(params)),
  create: (data) => api.post('/press', data),
  getById: (item_id) => api.get(`/press/${item_id}`),
  update: (item_id, data) => api.put(`/press/${item_id}`, data),
  delete: (item_id) => api.delete(`/press/${item_id}`),
};

// ============================================================
// SURVEYS & FEEDBACK
// ============================================================
export const surveysAPI = {
  getAll: (params = {}) => api.get('/surveys', withParams(params)),
  create: (data) => api.post('/surveys', data),
  getById: (item_id) => api.get(`/surveys/${item_id}`),
  update: (item_id, data) => api.put(`/surveys/${item_id}`, data),
  delete: (item_id) => api.delete(`/surveys/${item_id}`),
};

// ============================================================
// EVENTS
// ============================================================
export const eventsAPI = {
  getUpcoming: () => api.get('/events/upcoming'),
  getAll: (params = {}) => api.get('/events', withParams(params)),
  create: (data) => api.post('/events', data),
  getById: (item_id) => api.get(`/events/${item_id}`),
  update: (item_id, data) => api.put(`/events/${item_id}`, data),
  delete: (item_id) => api.delete(`/events/${item_id}`),
};

// ============================================================
// NOTES & FOLLOW-UPS
// ============================================================
export const notesFollowupsAPI = {
  getAll: (params = {}) => api.get('/notes-followups', withParams(params)),
  create: (data) => api.post('/notes-followups', data),
  getById: (item_id) => api.get(`/notes-followups/${item_id}`),
  update: (item_id, data) => api.put(`/notes-followups/${item_id}`, data),
  delete: (item_id) => api.delete(`/notes-followups/${item_id}`),
};

// ============================================================
// LOCAL LEADERS
// ============================================================
export const localLeadersAPI = {
  getAll: (params = {}) => api.get('/local-leaders', withParams(params)),
  create: (data) => api.post('/local-leaders', data),
  getById: (leader_id) => api.get(`/local-leaders/${leader_id}`),
  update: (leader_id, data) => api.put(`/local-leaders/${leader_id}`, data),
  delete: (leader_id) => api.delete(`/local-leaders/${leader_id}`),
  revealAadhaar: (leader_id) => api.get(`/local-leaders/${leader_id}/aadhaar/reveal`),
};

// ============================================================
// JANATA DARBAR
// ============================================================
export const janataDarbarAPI = {
  getAll: (params = {}) => api.get('/janata-darbar', withParams(params)),
  create: (data) => api.post('/janata-darbar', data),
  getById: (visit_id) => api.get(`/janata-darbar/${visit_id}`),
  update: (visit_id, data) => api.put(`/janata-darbar/${visit_id}`, data),
  delete: (visit_id) => api.delete(`/janata-darbar/${visit_id}`),
};

// ============================================================
// CONTACT US
// ============================================================
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  getMessages: (params = {}) => api.get('/contact-messages', withParams(params)),
  updateStatus: (message_id, status) =>
    api.put(`/contact-messages/${message_id}/status`, { status }),
};

// ============================================================
// PUBLIC
// ============================================================
export const publicAPI = {
  voterLookup: (epic_no) => api.get(`/public/voter-lookup/${epic_no}`),
};

// ============================================================
// ACHIEVEMENTS
// ============================================================
export const achievementsAPI = {
  getAll: (params = {}) => api.get('/achievements', withParams(params)),
  create: (data) => api.post('/achievements', data),
  getById: (achievement_id) => api.get(`/achievements/${achievement_id}`),
  update: (achievement_id, data) => api.put(`/achievements/${achievement_id}`, data),
  delete: (achievement_id) => api.delete(`/achievements/${achievement_id}`),
};

// ============================================================
// REPORTS
// ============================================================
export const reportsAPI = {
  getSummary: () => api.get('/reports/summary'),
  getSchemePerformance: () => api.get('/reports/scheme-performance'),
  getApplicationsTrend: () => api.get('/reports/applications-trend'),
};

// ============================================================
// DASHBOARD HOME
// ============================================================
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary'),
};

// ============================================================
// STAFF MANAGEMENT
// ============================================================
export const staffAPI = {
  getAll: (params = {}) => api.get('/staff', withParams(params)),
  create: (data) => api.post('/staff', data),
  getById: (staff_id) => api.get(`/staff/${staff_id}`),
  update: (staff_id, data) => api.put(`/staff/${staff_id}`, data),
  delete: (staff_id) => api.delete(`/staff/${staff_id}`),
};

// ============================================================
// SETTINGS
// ============================================================
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// ============================================================
// HEALTH
// ============================================================
export const healthAPI = {
  check: () => api.get('/health'),
};